import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  Drawer,
  Group,
  ScrollArea,
  SimpleGrid,
  Stack,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { entryInputSchema, type EntryInputSchema } from '../../lib/schemas';
import { useCreateEntry, useUpdateEntry } from '../../queries/runsheet-entries';
import { useDocumentTypes } from '../../queries/document-types';
import { useProspectsList } from '../../queries/prospects';
import { useTitleRun } from '../../queries/title-runs';
import { useTract } from '../../queries/tracts';
import { useEntryFormLayout } from '../../queries/entry-form-layout';
import { useProjectStore } from '../../project/project-store';
import {
  ENTRY_FORM_FIELDS,
  ENTRY_FORM_FIELD_NAMES,
  ENTRY_DEFAULT_LAYOUT,
} from '../../lib/form-layout/entry-fields';
import { FormLayoutRenderer } from '../../lib/form-layout/FormLayoutRenderer';
import { PartyPickerForEntry } from './PartyPickerForEntry';
import { EntryAttachments } from './EntryAttachments';
import { ApiError } from '../../lib/api-error';
import type { RunsheetEntry, Tract } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  titleRunId: string;
  entry?: RunsheetEntry | null;
  onSaved?: (e: RunsheetEntry) => void;
}

function emptyFor(titleRunId: string): EntryInputSchema {
  return {
    title_run_id: titleRunId,
    document_type_id: null,
    prospect_id: null,
    instrument_date: null,
    recording_date: null,
    book: null,
    page: null,
    instrument_number: null,
    legal_description: null,
    consideration: null,
    interest_conveyed: null,
    depth_limitation: null,
    area_limitation: null,
    reservations: null,
    township: null,
    range: null,
    section: null,
    block: null,
    abstract: null,
    survey: null,
    has_mineral_reservation: false,
    mineral_reservation_description: null,
    remarks: null,
    reviewer_notes: null,
  };
}

export function EntryFormDrawer({ opened, onClose, titleRunId, entry, onSaved }: Props) {
  const isEdit = !!entry;
  const createM = useCreateEntry();
  const updateM = useUpdateEntry();
  const { data: docTypes = [] } = useDocumentTypes();
  const { data: prospects = [] } = useProspectsList({ limit: 500 });
  const runQ = useTitleRun(opened && !isEdit ? titleRunId : null);
  const tractQ = useTract(runQ.data?.tract_id ?? null);
  const projectId = useProjectStore((s) => s.projectId);
  const layoutQ = useEntryFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? ENTRY_DEFAULT_LAYOUT;

  const form = useForm<EntryInputSchema>({
    resolver: zodResolver(entryInputSchema),
    defaultValues: emptyFor(titleRunId),
  });

  // Reset the form when the drawer opens or switches between entries. We do
  // NOT depend on tractQ.data here so a late-arriving tract response won't
  // wipe user edits; the second effect below applies tract defaults
  // non-destructively after the user-visible reset has already happened.
  useEffect(() => {
    if (!opened) return;
    form.reset(entry ? toFormValues(entry) : emptyFor(titleRunId));
  }, [opened, entry, titleRunId, form]);

  // Once the parent tract resolves, fill in any legal-anchor fields that the
  // user hasn't already filled in. We compare the current value to the empty
  // template so we never overwrite explicit overrides (including a user who
  // intentionally cleared a field).
  useEffect(() => {
    if (!opened || entry || !tractQ.data) return;
    const tract = tractQ.data;
    const cur = form.getValues();
    const fields: Array<keyof EntryInputSchema & keyof Tract> = [
      'township',
      'range',
      'section',
      'block',
      'abstract',
      'survey',
    ];
    for (const f of fields) {
      const tractVal = tract[f];
      if ((cur[f] === null || cur[f] === undefined || cur[f] === '') && tractVal != null) {
        form.setValue(f, tractVal, { shouldDirty: false });
      }
    }
  }, [opened, entry, tractQ.data, form]);

  async function onSubmit(values: EntryInputSchema) {
    try {
      const clean = cleanBlanks(values);
      // Belt + braces: when the flag is off, force the description to null
      // before sending so the API and DB CHECK never see an inconsistent pair.
      if (clean.has_mineral_reservation === false) {
        clean.mineral_reservation_description = null;
      }
      const res = isEdit
        ? await updateM.mutateAsync({ id: entry!.id, patch: clean })
        : await createM.mutateAsync(clean);
      notifications.show({
        title: isEdit ? 'Entry updated' : 'Entry added',
        message:
          res.entry.book && res.entry.page
            ? `${res.entry.book}/${res.entry.page}`
            : res.entry.id.slice(0, 8),
        color: 'pine',
      });
      onSaved?.(res.entry);
      if (!isEdit) onClose();
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit entry' : 'Add runsheet entry'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={ENTRY_FORM_FIELDS}
            allNames={ENTRY_FORM_FIELD_NAMES}
            context={{ docTypes, prospects }}
            isEdit={isEdit}
          />

          {isEdit && entry && (
            <>
              <Divider label="Parties" labelPosition="left" />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <PartyPickerForEntry entryId={entry.id} role="grantor" title="Grantors" />
                <PartyPickerForEntry entryId={entry.id} role="grantee" title="Grantees" />
              </SimpleGrid>

              <Divider label="Attached documents" labelPosition="left" />
              <EntryAttachments entryId={entry.id} />
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Close
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create entry'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function toFormValues(e: RunsheetEntry): EntryInputSchema {
  return {
    title_run_id: e.title_run_id,
    document_type_id: e.document_type_id,
    prospect_id: e.prospect_id,
    instrument_date: e.instrument_date,
    recording_date: e.recording_date,
    book: e.book,
    page: e.page,
    instrument_number: e.instrument_number,
    legal_description: e.legal_description,
    consideration: e.consideration,
    interest_conveyed: e.interest_conveyed,
    depth_limitation: e.depth_limitation,
    area_limitation: e.area_limitation,
    reservations: e.reservations,
    township: e.township,
    range: e.range,
    section: e.section,
    block: e.block,
    abstract: e.abstract,
    survey: e.survey,
    has_mineral_reservation: e.has_mineral_reservation,
    mineral_reservation_description: e.mineral_reservation_description,
    remarks: e.remarks,
    reviewer_notes: e.reviewer_notes,
  };
}

function cleanBlanks<T extends Record<string, unknown>>(v: T): T {
  const out: Record<string, unknown> = { ...v };
  for (const k of Object.keys(out)) {
    if (typeof out[k] === 'string' && (out[k] as string).trim() === '') out[k] = null;
  }
  return out as T;
}
